import { ActionIcon, Avatar, Menu, Tooltip } from "@mantine/core";
import IconCheck from "@src/assets/icons/tabler/check.svg?react";
import IconPlus from "@src/assets/icons/tabler/plus.svg?react";
import IconTrash from "@src/assets/icons/tabler/trash.svg?react";
import type { DesktopProfile } from "@src/electron-api";
import clsx from "clsx";
import { useCallback } from "react";
import { DEFAULT_DESKTOP_PROFILE_COLOR } from "../../constants";
import { useDesktopProfiles } from "../../hooks/use-desktop-profiles";
import { openCreateProfileModal } from "../modals/create-profile-modal";
import { openDeleteProfileModal } from "../modals/delete-profile-modal";

export interface DesktopUserMenuItemsProps {
	currentAvatarUrl?: string | null;
}

export function DesktopUserMenuItems({
	currentAvatarUrl,
}: DesktopUserMenuItemsProps) {
	const {
		isElectron,
		profiles,
		currentProfileId,
		switchProfile,
		deleteProfile,
	} = useDesktopProfiles();

	const handleSelectProfile = useCallback(
		async (profile: DesktopProfile) => {
			await switchProfile(profile.id);
		},
		[switchProfile],
	);

	if (!isElectron || profiles.length === 0) {
		return null;
	}

	return (
		<>
			<Menu.Label>Profiles</Menu.Label>
			{profiles.map((p) => {
				const isCurrent = p.id === currentProfileId;
				const avatarSrc = isCurrent
					? (currentAvatarUrl ?? p.avatarUrl)
					: p.avatarUrl;
				const profileColor = p.color || DEFAULT_DESKTOP_PROFILE_COLOR;

				return (
					<Menu.Item
						key={p.id}
						onClick={() => handleSelectProfile(p)}
						leftSection={
							<div className="relative shrink-0">
								<Avatar
									src={avatarSrc}
									size={28}
									radius="xl"
									style={{
										border: `2px solid ${profileColor}`,
									}}
								>
									{p.name[0]?.toUpperCase()}
								</Avatar>
								<div
									className={clsx(
										"absolute -bottom-0.5 -right-0.5",
										"w-2.5 h-2.5 rounded-full",
										"border border-surface-default",
									)}
									style={{ backgroundColor: profileColor }}
								/>
							</div>
						}
						rightSection={
							isCurrent ? (
								<IconCheck className="w-3.5 h-3.5 text-brand-primary shrink-0" />
							) : profiles.length > 1 ? (
								<Tooltip
									label={`Delete ${p.name}`}
									position="left"
									withArrow
									withinPortal
								>
									<ActionIcon
										size="xs"
										variant="subtle"
										color="gray"
										className={clsx(
											"text-text-tertiary hover:text-danger",
											"hover:bg-danger/10 transition-colors",
										)}
										onClick={(e) => {
											e.stopPropagation();
											openDeleteProfileModal({
												profile: p,
												onDelete: () => deleteProfile(p.id),
											});
										}}
										aria-label={`Delete ${p.name}`}
									>
										<IconTrash className="w-3.5 h-3.5" />
									</ActionIcon>
								</Tooltip>
							) : null
						}
						className={clsx(isCurrent && "bg-surface-subtle")}
					>
						<div className="flex flex-col min-w-0 pr-1">
							<span
								className={clsx(
									"text-xs leading-tight overflow-hidden",
									"text-ellipsis whitespace-nowrap",
									isCurrent
										? "font-semibold text-text-primary"
										: "text-text-primary",
								)}
							>
								{p.name}
							</span>
							<span
								className={clsx(
									"text-[11px] leading-tight text-text-tertiary",
									"overflow-hidden text-ellipsis whitespace-nowrap",
									"mt-0.5",
								)}
							>
								{p.email || (isCurrent ? "Active window" : "Not signed in")}
							</span>
						</div>
					</Menu.Item>
				);
			})}

			<Menu.Item
				leftSection={
					<div
						className={clsx(
							"w-7 h-7 rounded-full border border-dashed",
							"border-border-default flex items-center justify-center",
							"text-text-secondary",
						)}
					>
						<IconPlus className="w-3.5 h-3.5" />
					</div>
				}
				onClick={() => openCreateProfileModal()}
			>
				<span className="text-xs text-text-secondary">Add Profile...</span>
			</Menu.Item>
			<Menu.Divider />
		</>
	);
}
